/**
 * CassKai - Utilities for fiscal period management
 * Gestion des périodes fiscales (années N, N-1, N-2, etc.)
 * Supporte les exercices décalés (ex: 01/06 → 31/05)
 */
import { logger } from '@/lib/logger';

export interface FiscalPeriod {
  label: string;
  value: string;
  startDate: Date;
  endDate: Date;
  year: number;
}
export type PeriodType = 'month' | 'quarter' | 'year' | 'custom';

/**
 * Convertit une date en format ISO pour Supabase (YYYY-MM-DD)
 * Utilise les getters locaux (getFullYear/getMonth/getDate) pour éviter
 * le décalage timezone de toISOString() qui convertit en UTC.
 */
export function toISODateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Crée une Date pour le dernier jour d'un mois donné, timezone-safe.
 * Utilise le pattern `new Date(year, month, 0)` où month est 1-indexé (1=Jan, 12=Dec)
 * pour obtenir le dernier jour du mois précédent (donc month 13 → dernier jour de dec).
 */
function lastDayOfMonth(year: number, month: number): Date {
  // new Date(year, month, 0) → dernier jour du mois (month est 1-indexé ici)
  return new Date(year, month, 0, 23, 59, 59, 999);
}

/**
 * Génère les périodes fiscales disponibles (N, N-1, N-2, etc.)
 * Supporte les exercices décalés via fiscalStartMonth.
 *
 * @param yearsBack Nombre d'années à remonter (par défaut 5)
 * @param fiscalStartMonth Mois de début de l'exercice (1=janvier, 7=juillet, etc.)
 * @returns Liste des périodes fiscales
 */
export function getFiscalPeriods(yearsBack: number = 5, fiscalStartMonth: number = 1): FiscalPeriod[] {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1-12
  const periods: FiscalPeriod[] = [];

  // Déterminer l'exercice fiscal courant
  // Si exercice décalé et qu'on est avant le mois de début, on est dans l'exercice N-1
  let currentFiscalEndYear = currentYear;
  if (fiscalStartMonth > 1 && currentMonth < fiscalStartMonth) {
    currentFiscalEndYear = currentYear;
  } else if (fiscalStartMonth > 1) {
    currentFiscalEndYear = currentYear + 1;
  }

  for (let i = 0; i <= yearsBack; i++) {
    const endYear = currentFiscalEndYear - i;
    const label = i === 0 ? `Année N (${endYear})` : `Année N-${i} (${endYear})`;

    let startDate: Date;
    let endDate: Date;

    if (fiscalStartMonth === 1) {
      // Exercice année civile : 01/01 → 31/12
      startDate = new Date(endYear, 0, 1);
      endDate = lastDayOfMonth(endYear, 12);
    } else {
      // Exercice décalé : début mois M de l'année N-1, fin mois M-1 de l'année N
      const startYear = endYear - 1;
      startDate = new Date(startYear, fiscalStartMonth - 1, 1);
      endDate = lastDayOfMonth(endYear, fiscalStartMonth - 1);
    }

    periods.push({
      label,
      value: i === 0 ? 'N' : `N-${i}`,
      startDate,
      endDate,
      year: endYear
    });
  }
  return periods;
}
/**
 * Obtient une période fiscale spécifique par sa valeur
 * @param periodValue Valeur de la période (ex: "N", "N-1", "N-2")
 * @param fiscalStartMonth Mois de début de l'exercice (1-12)
 * @returns Période fiscale ou null si non trouvée
 */
export function getFiscalPeriod(periodValue: string, fiscalStartMonth: number = 1): FiscalPeriod | null {
  const periods = getFiscalPeriods(5, fiscalStartMonth);
  return periods.find(p => p.value === periodValue) || null;
}
/**
 * Obtient une période fiscale par année
 * @param year Année fiscale (année de fin de l'exercice)
 * @param fiscalStartMonth Mois de début de l'exercice (1-12)
 * @returns Période fiscale ou null si non trouvée
 */
export function getFiscalPeriodByYear(year: number, fiscalStartMonth: number = 1): FiscalPeriod | null {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  let currentFiscalEndYear = currentYear;
  if (fiscalStartMonth > 1 && currentMonth >= fiscalStartMonth) {
    currentFiscalEndYear = currentYear + 1;
  }

  const offset = currentFiscalEndYear - year;
  if (offset < 0 || offset > 5) return null;

  const value = offset === 0 ? 'N' : `N-${offset}`;
  const label = offset === 0 ? `Année N (${year})` : `Année N-${offset} (${year})`;

  let startDate: Date;
  let endDate: Date;

  if (fiscalStartMonth === 1) {
    startDate = new Date(year, 0, 1);
    endDate = lastDayOfMonth(year, 12);
  } else {
    const startYear = year - 1;
    startDate = new Date(startYear, fiscalStartMonth - 1, 1);
    endDate = lastDayOfMonth(year, fiscalStartMonth - 1);
  }

  return { label, value, startDate, endDate, year };
}
/**
 * Formate une période pour affichage
 * @param period Période fiscale
 * @param locale Locale (par défaut 'fr-FR')
 * @returns Chaîne formatée (ex: "Période du 01/01/2025 au 31/12/2025")
 */
export function formatPeriod(period: FiscalPeriod, locale: string = 'fr-FR'): string {
  const startStr = period.startDate.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  const endStr = period.endDate.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  return `Période du ${startStr} au ${endStr}`;
}
/**
 * Obtient les périodes mensuelles pour une année donnée
 * @param year Année
 * @returns Liste des 12 mois
 */
export function getMonthlyPeriods(year: number): Array<{ label: string; value: string; month: number; startDate: Date; endDate: Date }> {
  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  return months.map((name, index) => {
    const month = index; // 0-based for Date constructor
    const startDate = new Date(year, month, 1);
    const endDate = lastDayOfMonth(year, month + 1); // month+1 en 1-indexé
    return {
      label: `${name} ${year}`,
      value: `${year}-${String(month + 1).padStart(2, '0')}`,
      month: month + 1,
      startDate,
      endDate
    };
  });
}
/**
 * Obtient les périodes trimestrielles pour une année donnée
 * @param year Année
 * @returns Liste des 4 trimestres
 */
export function getQuarterlyPeriods(year: number): Array<{ label: string; value: string; quarter: number; startDate: Date; endDate: Date }> {
  return [
    {
      label: `T1 ${year}`,
      value: `${year}-Q1`,
      quarter: 1,
      startDate: new Date(year, 0, 1),
      endDate: lastDayOfMonth(year, 3) // 31 mars
    },
    {
      label: `T2 ${year}`,
      value: `${year}-Q2`,
      quarter: 2,
      startDate: new Date(year, 3, 1),
      endDate: lastDayOfMonth(year, 6) // 30 juin
    },
    {
      label: `T3 ${year}`,
      value: `${year}-Q3`,
      quarter: 3,
      startDate: new Date(year, 6, 1),
      endDate: lastDayOfMonth(year, 9) // 30 septembre
    },
    {
      label: `T4 ${year}`,
      value: `${year}-Q4`,
      quarter: 4,
      startDate: new Date(year, 9, 1),
      endDate: lastDayOfMonth(year, 12) // 31 décembre
    }
  ];
}
/**
 * Détermine si une année est une année fiscale future
 * @param year Année à vérifier
 * @returns true si l'année est dans le futur
 */
export function isFutureYear(year: number): boolean {
  const currentYear = new Date().getFullYear();
  return year > currentYear;
}
/**
 * Obtient l'année fiscale en cours
 * @returns Année en cours
 */
export function getCurrentFiscalYear(): number {
  return new Date().getFullYear();
}
/**
 * Debug utility: Log period information
 * @param period Période à logger
 */
export function debugPeriod(period: FiscalPeriod | null): void {
  if (!period) {
    logger.debug('Periods', '🔍 [Period Debug] Period is null');
    return;
  }
  logger.debug('periods', '🔍 [Period Debug]:', {
    label: period.label,
    value: period.value,
    year: period.year,
    startDate: toISODateString(period.startDate),
    endDate: toISODateString(period.endDate),
    currentYear: getCurrentFiscalYear(),
    isFuture: isFutureYear(period.year)
  });
}
